/**
 * Copyright ©2026 Drivepoint
 */

import useStateChange from "@hooks/useStateChange";
import LaunchDarklyClient from "@services/clients/LaunchDarklyClient";
import DrivepointUser from "@services/user/DrivepointUser";

export const NEO_UI_FLAG = "neo_ui";

const NEO_BASE = "/neo";

// First path segments that are not a company id. Sign-in, quickstart and dev pages stay in webapp-mui.
const NON_COMPANY_SEGMENTS: ReadonlySet<string> = new Set([
  "administration",
  "dev",
  "firebase_action",
  "intuit_disconnect",
  "invite_user",
  "quickstart",
  "shopify",
  "shopify_connect",
  "sign_in",
  "sign_in_custom_token",
  "sign_up"
]);

// webapp-mui page (path after /:company) → Neo page. Unlisted company pages open the Neo home.
const COMPANY_PAGES: ReadonlyArray<[RegExp, (match: RegExpMatchArray) => string]> = [
  [/^administration\/sources$/, () => "sources"],
  [/^administration\/sources\/csv-import$/, () => "sources/csv-import/new"],
  [/^administration\/sources\/csv-import\/([^/]+)$/, (match) => `sources/csv-import/${match[1]}`],
  [/^administration\/sources\/tiktok\/shop_selector$/, () => "administration/sources/tiktok/shop_selector"],
  [/^reports$/, () => "reports"],
  [/^products$/, () => "products"],
  [/^team$/, () => "users"],
  [/^team\/([^/]+)$/, (match) => `users/${match[1]}`],
  [/^settings$/, () => "settings"]
];

export function companyFromPath(pathname: string): string | undefined {
  const company = pathname.split("/")[1];
  return company && !NON_COMPANY_SEGMENTS.has(company) ? company : undefined;
}

/** The Neo URL for a webapp-mui location, or null when the page has no Neo equivalent. */
export function neoUrl({pathname, search, hash}: {pathname: string; search: string; hash: string}): string | null {
  const query = `${search}${hash}`;
  if (pathname === "/") { return `${NEO_BASE}/${query}`; }
  if (pathname === "/administration/sources/tiktok/callback") { return `${NEO_BASE}${pathname}${query}`; }
  const company = companyFromPath(pathname);
  if (!company) { return null; }
  const page = pathname.replace(/\/+$/, "").split("/").slice(2).join("/");
  for (const [pattern, toNeoPage] of COMPANY_PAGES) {
    const match = page.match(pattern);
    if (match) { return `${NEO_BASE}/${company}/${toNeoPage(match)}${query}`; }
  }
  return `${NEO_BASE}/${company}`;
}

/** Leaves webapp-mui for Neo, keeping the query (and any single-use auth code) intact. */
export function redirectToNeo(): boolean {
  const url = neoUrl(window.location);
  if (!url) { return false; }
  window.location.replace(url);
  return true;
}

/**
 * Whether the neo_ui flag is on for the signed-in user (and companyId, when given). Undefined until
 * LaunchDarkly has evaluated flags for them; before sign-in flags belong to the anonymous context.
 */
export function useNeoUi(companyId?: string): boolean | undefined {
  const user = useStateChange<DrivepointUser>("user");
  const flags = useStateChange<any>("flags");
  const context: any = LaunchDarklyClient.client.getContext();
  const email = user?.user?.email;
  if (!email || !flags || context?.key !== email) { return undefined; }
  if (companyId && context.company_id !== companyId) { return undefined; }
  return flags[NEO_UI_FLAG] === true;
}
